// The day-by-day ledger: every inflow and outflow with its state, and the
// projected closing balance. The red days and the cash needed are
// cashplan_page_v1's; this only draws them.
//
// Forecast money is drawn lighter and in italics wherever it appears, and a
// balance column without forecast sits beside the projected one, so a forecast
// can never be read as committed.
import { useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Checkbox } from '../../lib/shadcn/checkbox'
import { cn } from '../../lib/shadcn/utils'
import { Chip } from '../interviews/Chip'
import { StateChip } from './StateChip'
import {
  AMOUNT_UNKNOWN, cashNeededLabel, formatDay, formatZar, isForecast, visibleDays,
  type LedgerDay, type LedgerItem, type ObligationItem,
} from '../../utils/cashPlan'

function ItemLine({ item, onEdit }: { item: LedgerItem; onEdit: (i: ObligationItem) => void }) {
  const forecast = isForecast(item)
  const amount = item.known ? formatZar(item.amount_zar) : AMOUNT_UNKNOWN
  if (item.kind === 'uber') {
    return (
      <li className={cn('flex flex-wrap items-baseline gap-x-2 gap-y-1 py-1', forecast && 'italic text-muted-foreground')}>
        <span className="w-24 shrink-0 text-right tabular-nums text-emerald-700 dark:text-emerald-300" style={forecast ? { opacity: 0.7 } : undefined}>
          {item.known ? `+${amount}` : amount}
        </span>
        <span className="min-w-0 flex-1">{item.label}</span>
        <StateChip state={item.state} />
        {item.state === 'earned' && !item.reports_complete && <Chip tone="amber">Reports incomplete</Chip>}
        {item.note && <span className="basis-full pl-[6.5rem] text-xs text-muted-foreground not-italic">{item.note}</span>}
      </li>
    )
  }
  return (
    <li className={cn('flex flex-wrap items-baseline gap-x-2 gap-y-1 py-1', item.skipped && 'line-through opacity-60')}>
      <span className={cn('w-24 shrink-0 text-right tabular-nums', item.known ? 'text-foreground' : 'text-amber-700 dark:text-amber-300')}>
        {item.known ? `-${amount}` : amount}
      </span>
      <button type="button" className="min-w-0 flex-1 text-left underline-offset-2 hover:underline" onClick={() => onEdit(item)}>
        {item.label}
      </button>
      {item.estimate && <Chip tone="amber">Estimate</Chip>}
      {item.override_id && <Chip tone="brand">{item.skipped ? 'Skipped' : 'Changed for this date'}</Chip>}
      <span className="basis-full pl-[6.5rem] text-xs text-muted-foreground">
        {item.known ? item.basis : `Needs: ${item.needs ?? 'an amount entered for this date'}`}
      </span>
    </li>
  )
}

function DayBlock({ d, onEdit }: { d: LedgerDay; onEdit: (i: ObligationItem) => void }) {
  const red = d.below_buffer
  const needed = cashNeededLabel(d)
  return (
    <div className={cn(
      'border-b border-border px-3 py-2 sm:px-4',
      red && 'bg-rose-50 dark:bg-rose-950/40',
      d.is_today && 'ring-1 ring-inset ring-[hsl(var(--brand))]',
      d.before_today && 'opacity-80',
    )}>
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <span className="w-24 shrink-0 font-medium text-foreground">{formatDay(d.date)}</span>
        {d.is_today && <Chip tone="brand">Today</Chip>}
        {d.before_today && <span className="text-xs text-muted-foreground">Before today, projected</span>}
        <span className="ml-auto flex flex-wrap items-baseline gap-x-4 text-sm tabular-nums">
          <span className={cn('font-semibold', d.balance_projected_zar < 0 ? 'text-rose-700 dark:text-rose-300' : 'text-foreground')}>
            {formatZar(d.balance_projected_zar)}
          </span>
          <span className="text-xs text-muted-foreground" title="Balance without forecast Uber money">
            without forecast {formatZar(d.balance_committed_zar)}
          </span>
        </span>
      </div>
      {needed && (
        <p className="mt-1 flex flex-wrap items-center gap-x-1 text-sm font-medium text-rose-800 dark:text-rose-200">
          <AlertTriangle className="h-4 w-4" /> {needed}
          {d.below_zero && d.cash_needed_to_zero_zar !== null && (
            <span className="font-normal text-rose-700 dark:text-rose-300">({formatZar(d.cash_needed_to_zero_zar)} just to reach zero)</span>
          )}
        </p>
      )}
      {d.unknown_out_so_far > 0 && d.unknown_out > 0 && (
        <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
          The balance leaves out {d.unknown_out_so_far} outflow{d.unknown_out_so_far === 1 ? '' : 's'} so far with an unknown amount.
        </p>
      )}
      {d.items.length > 0 && (
        <ul className="mt-1 text-sm">
          {d.items.map((it, i) => <ItemLine key={i} item={it} onEdit={onEdit} />)}
        </ul>
      )}
    </div>
  )
}

export function Ledger({ days, buffer, onEdit }: { days: LedgerDay[]; buffer: number; onEdit: (i: ObligationItem, date: string) => void }) {
  const [showQuiet, setShowQuiet] = useState(false)
  const shown = visibleDays(days, showQuiet)
  const red = days.filter((d) => d.below_buffer && !d.before_today).length
  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-3 py-2 text-xs text-muted-foreground sm:px-4">
        <span>
          Red is a day the projected balance is below the {formatZar(buffer)} buffer or below zero.
          {red > 0 ? ` ${red} red day${red === 1 ? '' : 's'} from today.` : ' No red day from today.'}
        </span>
        <label className="ml-auto inline-flex items-center gap-2">
          <Checkbox checked={showQuiet} onCheckedChange={(v) => setShowQuiet(v === true)} /> Show days with nothing moving
        </label>
      </div>
      {shown.length === 0
        ? <p className="px-4 py-6 text-sm text-muted-foreground">Nothing moves in this period.</p>
        : shown.map((d) => <DayBlock key={d.date} d={d} onEdit={(i) => onEdit(i, d.date)} />)}
    </div>
  )
}
